import { useState, useEffect } from 'react'
import { getDailyCompletions } from '../utils/storage'
import { LAUNCH_DATE, getPuzzleNumber } from '../utils/dailyPuzzleGen'
import { posterUrl, profileUrl } from '../api/tmdb'
import {
  IconX, IconPlay, IconChart, IconTicket, IconCheck, IconLock, IconChevronRight,
} from './icons'

const ACTOR_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='36' height='36' viewBox='0 0 36 36'%3E%3Crect fill='%23333' width='36' height='36' rx='18'/%3E%3C/svg%3E"
const MOVIE_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='36' viewBox='0 0 24 36'%3E%3Crect fill='%23333' width='24' height='36'/%3E%3C/svg%3E"

function localDateStr(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function buildDateList() {
  const launch = new Date(LAUNCH_DATE + 'T00:00:00')
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const yesterday = new Date(today.getTime() - 86400000)
  const dates = []
  let d = new Date(launch)
  while (d <= yesterday) {
    dates.push(localDateStr(d))
    d.setDate(d.getDate() + 1)
  }
  return dates.reverse() // newest first
}

function fmtDate(dateStr) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

function fmtTime(secs) {
  if (secs == null) return ''
  const m = String(Math.floor(secs / 60)).padStart(2, '0')
  const s = String(secs % 60).padStart(2, '0')
  return `${m}:${s}`
}

// "2026-06" — stable key for grouping/toggling a calendar month.
function monthKey(dateStr) {
  const d = new Date(dateStr + 'T00:00:00')
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

// "June 2026" — the section header label.
function monthLabel(dateStr) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'long', year: 'numeric',
  })
}

// Remembers which month sections are expanded across mounts within a session —
// the screen unmounts on navigation, so component state alone would forget.
let sessionExpandedMonths = null

export default function HistoricalPuzzles({ tickets, onPlay, onOpenStore, onBack }) {
  // dialog: { date, number, prevComp } — prevComp is the existing completion if replaying
  const [dialog, setDialog] = useState(null)
  const [loading, setLoading] = useState(false)
  const [showStats, setShowStats] = useState(false)

  const completions = getDailyCompletions()
  const dates = buildDateList()

  // Group the newest-first dates into calendar months (order preserved, so
  // months[0] is the most recent and each month's dates stay newest-first).
  const months = []
  for (const date of dates) {
    const key = monthKey(date)
    let g = months[months.length - 1]
    if (!g || g.key !== key) { g = { key, label: monthLabel(date), dates: [] }; months.push(g) }
    g.dates.push(date)
  }

  // Collapsible sections: most-recent month open by default, older ones closed.
  // The open set survives navigation this session via the module-level mirror.
  const [expanded, setExpanded] = useState(() => {
    if (sessionExpandedMonths) return new Set(sessionExpandedMonths)
    return new Set(dates.length ? [monthKey(dates[0])] : [])
  })
  useEffect(() => { sessionExpandedMonths = expanded }, [expanded])
  const toggleMonth = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const openDialog = (date, prevComp) => {
    setShowStats(false)
    setDialog({ date, number: getPuzzleNumber(date), prevComp: prevComp ?? null })
  }

  const closeDialog = () => {
    setShowStats(false)
    setDialog(null)
  }

  const handleConfirm = async () => {
    if (!dialog) return
    setLoading(true)
    try {
      await onPlay(dialog.date, dialog.number)
    } catch {
      setLoading(false)
      setDialog(null)
    }
  }

  const isReplay = !!dialog?.prevComp
  const canAfford = tickets >= 1

  return (
    <div className="hp-screen">
      <div className="screen-content">
        <div className="hp-header">
          <h2 className="hp-title">Historical Puzzles</h2>
          <p className="hp-sub">Revisit past shoots</p>
          <p className="hp-balance">
            <IconTicket size={12} aria-hidden="true" />
            {' '}{tickets.toLocaleString()} ticket{tickets !== 1 ? 's' : ''}
          </p>
        </div>

      {dates.length === 0 ? (
        <p className="hp-empty">No past puzzles yet — check back tomorrow!</p>
      ) : (
        <div className="hp-list">
          {months.map((month) => {
            const isOpen = expanded.has(month.key)
            return (
              <div key={month.key} className="hp-month">
                <button
                  type="button"
                  className={`hp-month__header${isOpen ? ' hp-month__header--open' : ''}`}
                  onClick={() => toggleMonth(month.key)}
                  aria-expanded={isOpen}
                >
                  <span className="hp-month__title">
                    {month.label}
                    <span className="hp-month__count">
                      {' · '}{month.dates.length} puzzle{month.dates.length !== 1 ? 's' : ''}
                    </span>
                  </span>
                  <span className="hp-month__chevron" aria-hidden="true">
                    <IconChevronRight size={16} />
                  </span>
                </button>

                {isOpen && (
                  <div className="hp-month__body">
                    {month.dates.map((date) => {
                      const num = getPuzzleNumber(date)
                      const comp = completions[date]

                      return (
                        <button
                          key={date}
                          type="button"
                          className={`hp-entry${comp ? ' hp-entry--done' : ' hp-entry--play'}`}
                          onClick={() => openDialog(date, comp)}
                        >
                          <div className="hp-entry__left">
                            <span className="hp-entry__num">Daily #{num}</span>
                            <span className="hp-entry__date">{fmtDate(date)}</span>
                          </div>
                          <div className="hp-entry__right">
                            {comp ? (
                              <>
                                <span className="hp-entry__check" aria-label="Completed">
                                  <IconCheck size={16} />
                                </span>
                                <span className="hp-entry__score">
                                  {comp.moves} hops · {fmtTime(comp.timeSeconds) || comp.time}
                                </span>
                              </>
                            ) : (
                              <>
                                <span className="hp-entry__lock" aria-label="Locked">
                                  <IconLock size={14} />
                                </span>
                                <span className="hp-entry__cost">
                                  <IconTicket size={12} aria-hidden="true" /> 1
                                </span>
                              </>
                            )}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
      </div>{/* end screen-content */}

      {dialog && (
        <div className="hp-overlay" onClick={() => !loading && closeDialog()}>
          <div className="hp-modal" onClick={(e) => e.stopPropagation()}>

            {showStats ? (
              /* ── Stats panel ── */
              <>
                <p className="hp-modal__title">Daily #{dialog.number}</p>
                <p className="hp-modal__sub">{fmtDate(dialog.date)}</p>

                <div className="hp-stats-grid">
                  <div className="hp-stat-tile">
                    <span className="hp-stat-tile__value">{dialog.prevComp.moves}</span>
                    <span className="hp-stat-tile__label">Hops</span>
                  </div>
                  <div className="hp-stat-tile">
                    <span className="hp-stat-tile__value">
                      {fmtTime(dialog.prevComp.timeSeconds) || dialog.prevComp.time || '—'}
                    </span>
                    <span className="hp-stat-tile__label">Time</span>
                  </div>
                  <div className="hp-stat-tile">
                    <span className="hp-stat-tile__value">{dialog.prevComp.hintsUsed ?? 0}</span>
                    <span className="hp-stat-tile__label">Cue Cards</span>
                  </div>
                </div>

                {dialog.prevComp.chain && dialog.prevComp.chain.length > 0 && (
                  <div className="hp-chain-wrap">
                    <div className="hp-chain">
                      {dialog.prevComp.chain.map((item, i) => (
                        <div key={i} className="hp-chain__segment">
                          {item.type === 'actor' ? (
                            <div className="hp-chain__cell">
                              <div className="hp-chain__actor">
                                <img
                                  src={item.profile_path ? profileUrl(item.profile_path, 'w45') : ACTOR_PH}
                                  alt={item.name}
                                  onError={(e) => { e.currentTarget.src = ACTOR_PH }}
                                />
                              </div>
                              <span className="hp-chain__name">{item.name}</span>
                            </div>
                          ) : (
                            <div className="hp-chain__cell">
                              <div className="hp-chain__movie">
                                <img
                                  src={item.poster_path ? posterUrl(item.poster_path, 'w92') : MOVIE_PH}
                                  alt={item.title}
                                  onError={(e) => { e.currentTarget.src = MOVIE_PH }}
                                />
                              </div>
                              <span className="hp-chain__name">{item.title}</span>
                            </div>
                          )}
                          {i < dialog.prevComp.chain.length - 1 && (
                            <span className="hp-chain__arrow">›</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  className="btn-ghost hp-modal__btn"
                  onClick={() => setShowStats(false)}
                >
                  Back
                </button>
              </>
            ) : (
              /* ── Normal confirm panel ── */
              <>
                <p className="hp-modal__title">
                  {isReplay ? 'Replay' : 'Play'} Daily #{dialog.number}
                </p>
                <p className="hp-modal__sub">{fmtDate(dialog.date)}</p>

                {isReplay && (
                  <p className="hp-modal__best">
                    Your best: <strong>{dialog.prevComp.moves} hops</strong>
                    {' — '}
                    <strong>{fmtTime(dialog.prevComp.timeSeconds) || dialog.prevComp.time}</strong>
                  </p>
                )}

                <p className="hp-modal__cost">
                  <IconTicket size={12} aria-hidden="true" />
                  {' '}1 Ticket · You have {tickets.toLocaleString()}
                </p>

                {!canAfford && (
                  <p className="hp-modal__err">Not enough tickets</p>
                )}

                {/* Action row: yin/yang circles. Left = Maybe Later (cancel),
                    center = primary action (Action! when affordable, Get Tickets
                    when broke), right = My Stats (replay only). Equal-width
                    columns keep the primary dead-centered. */}
                <div className="hp-modal__actions">
                  <div className="hp-action">
                    <button
                      type="button"
                      className="hp-circle hp-circle--inverse"
                      onClick={closeDialog}
                      disabled={loading}
                      aria-label="Maybe Later"
                      title="Maybe Later"
                    >
                      <IconX size={22} />
                    </button>
                    <span className="hp-action__label">Maybe<br />Later</span>
                  </div>

                  <div className="hp-action">
                    {canAfford ? (
                      <button
                        type="button"
                        className="hp-circle hp-circle--primary"
                        onClick={handleConfirm}
                        disabled={loading}
                        aria-label="Action — play puzzle"
                        title="Action!"
                      >
                        <IconPlay size={22} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="hp-circle hp-circle--primary"
                        onClick={() => { closeDialog(); onOpenStore() }}
                        aria-label="Get tickets"
                        title="Get Tickets"
                      >
                        <IconTicket size={22} />
                      </button>
                    )}
                    <span className="hp-action__label">
                      {canAfford ? (loading ? 'Loading…' : 'Action!') : 'Get Tickets'}
                    </span>
                  </div>

                  {isReplay && (
                    <div className="hp-action">
                      <button
                        type="button"
                        className="hp-circle hp-circle--inverse"
                        onClick={() => setShowStats(true)}
                        disabled={loading}
                        aria-label="My Stats"
                        title="My Stats"
                      >
                        <IconChart size={22} />
                      </button>
                      <span className="hp-action__label">My<br />Stats</span>
                    </div>
                  )}
                </div>
              </>
            )}

          </div>
        </div>
      )}
    </div>
  )
}
